<template>
  <div class="search-overlay" v-if="isVisible" @click.self="close">
    <div class="search-modal">
      <div class="search-header">
        <h2>{{ t('search.title') }}</h2>
        <button class="close-btn" @click="close">×</button>
      </div>
      <div class="search-input-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          :placeholder="t('search.placeholder')" 
          @input="handleSearch"
          ref="searchInput"
          class="search-input"
        />
      </div>
      <div class="search-results" v-if="searchQuery.length > 0">
        <div v-if="loading" class="search-loading">
          {{ t('search.loading') }}
        </div>
        <div v-else-if="results.length === 0" class="search-no-results">
          {{ t('search.noResults') }}
        </div>
        <div v-else class="search-results-list">
          <div 
            v-for="(result, index) in results" 
            :key="index" 
            class="search-result-item"
            @click="navigateToResult(result)"
          >
            <div class="result-title" v-html="highlightText(result.title)"></div>
            <div class="result-path">{{ result.path }}</div>
            <div class="result-content" v-html="highlightText(result.content)"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t, locale } = useI18n()
const router = useRouter()

const JUNK_PATH_PARTS = ['pages', 'header', 'footer', 'home', 'buttons', 'search', 'default'];

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const searchQuery = ref('')
const results = ref<any[]>([])
const loading = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)
const searchData = ref<any>(null)

// 关闭搜索模态框
const close = () => {
  searchQuery.value = ''
  results.value = []
  emit('close')
}

// 处理搜索
const handleSearch = async () => {
  if (searchQuery.value.length < 2) {
    results.value = []
    return
  }

  loading.value = true

  try {
    // 如果还没有加载数据，先加载数据
    if (!searchData.value) {
      await loadSearchData()
    }

    // 执行搜索
    const query = searchQuery.value.toLowerCase()
    results.value = searchInData(query)
  } catch (error) {
    console.error('Search error:', error)
  } finally {
    loading.value = false
  }
}

// 加载搜索数据
const loadSearchData = async () => {
  try {
    // 从 'zh-CN' 或 'en-US' 中获取 'zh' 或 'en'
    const lang = locale.value.split('-')[0];
    const response = await fetch(`/jsonsearch/index.${lang}.json`)
    if (!response.ok) {
      throw new Error(`Failed to fetch search index for locale: ${lang}`);
    }
    searchData.value = await response.json()
  } catch (error) {
    console.error('Error loading search data:', error)
    searchData.value = null // Reset on error
  }
}

// 在数据中搜索
const searchInData = (query: string) => {
  const searchResults: any[] = []
  
  // 递归搜索对象
  const searchObject = (obj: any, path: string[] = [], parentTitle: string = '') => {
    // Case 1: The object is a self-contained news article.
    // We treat it as a single search unit and do not recurse further into its properties.
    if (typeof obj === 'object' && obj !== null && typeof obj.url === 'string' && obj.url.startsWith('/news/')) {
        const articleText = (obj.title + ' ' + (obj.content || '')).toLowerCase();
        if (articleText.includes(query)) {
            searchResults.push({
                title: obj.title,
                path: obj.url,
                content: obj.content || '',
                key: path.join('.'),
                url: obj.url
            });
        }
        return; // Stop processing this branch, as it's a complete article
    }

    // Case 2: The object is not a news article, so we iterate its properties.
    if (typeof obj !== 'object' || obj === null) {
        return;
    }

    for (const [key, value] of Object.entries(obj)) {
        const currentPath = [...path, key];

        if (typeof value === 'string') {
            // A plain string value to search in
            if (value.toLowerCase().includes(query) || key.toLowerCase().includes(query)) {
                const cleanPath = path.filter(p => !JUNK_PATH_PARTS.includes(p));
                searchResults.push({
                    title: obj.title || parentTitle || key,
                    path: '/' + cleanPath.join('/'), // The path of the container object
                    content: value,
                    key: currentPath.join('.')
                });
            }
        } else {
            // Another object or array, recurse into it
            searchObject(value, currentPath, obj.title || parentTitle || key);
        }
    }
  }
  
  searchObject(searchData.value)
  
  // 根据相关性排序结果
  return searchResults.sort((a, b) => {
    // 标题匹配优先级高于内容匹配
    const aTitleMatch = a.title.toLowerCase().includes(query) ? 1 : 0
    const bTitleMatch = b.title.toLowerCase().includes(query) ? 1 : 0
    
    if (aTitleMatch !== bTitleMatch) {
      return bTitleMatch - aTitleMatch
    }
    
    // 路径短的优先级高
    return a.path.length - b.path.length
  }).slice(0, 50) // 限制结果数量
}

// 高亮搜索文本并生成摘要
const highlightText = (text: string) => {
  if (!searchQuery.value || !text) return text;

  const query = searchQuery.value;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  
  const index = lowerText.indexOf(lowerQuery);

  // 如果找不到关键词或文本很短，则高亮全文
  if (index === -1 || text.length < 150) {
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  // 以关键词为中心截取摘要
  const snippetLength = 80; // 关键词前后各截取的长度
  const start = Math.max(0, index - snippetLength);
  const end = Math.min(text.length, index + query.length + snippetLength);
  
  let snippet = text.substring(start, end);

  // 全局高亮摘要中的关键词
  const regex = new RegExp(`(${query})`, 'gi');
  snippet = snippet.replace(regex, '<span class="highlight">$1</span>');

  // 在摘要前后加上省略号
  if (start > 0) {
    snippet = '... ' + snippet;
  }
  if (end < text.length) {
    snippet = snippet + ' ...';
  }
  
  return snippet;
};

// 导航到搜索结果
const navigateToResult = (result: any) => {
  // 优先使用结果中直接提供的url (主要用于新闻)
  if (result.url && result.url.startsWith('/news/')) {
    const resolvedRoute = router.resolve(result.url);
    if (resolvedRoute.matched.length > 0) {
      router.push(result.url);
      close();
      return;
    }
  }

  // 对于其他页面，使用基于 key 的贪心路径匹配策略
  const parts = result.key.split('.');
  const cleanParts = parts.filter((p: string) => !JUNK_PATH_PARTS.includes(p));

  // 从长到短尝试匹配有效路由
  for (let i = cleanParts.length; i > 0; i--) {
    const potentialPath = '/' + cleanParts.slice(0, i).join('/');
    const resolved = router.resolve(potentialPath);

    if (resolved.matched.length > 0 && resolved.name) {
      router.push(potentialPath);
      close();
      return;
    }
  }

  // 最终的回退
  router.push('/');
  close();
}


// 监听按键事件
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.isVisible) {
    close()
  }
}

// 当模态框显示时，聚焦到搜索输入框
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    nextTick(() => {
      if (searchInput.value) {
        searchInput.value.focus()
      }
    })
  }
})

// 监听语言环境变化以重新加载搜索数据
watch(locale, loadSearchData)

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  // 预加载搜索数据
  loadSearchData()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped lang="scss">
.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
  z-index: 1000;
  overflow-y: auto;
}

.search-modal {
  width: 100%;
  max-width: 700px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    
    &:hover {
      color: #333;
    }
  }
}

.search-input-container {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  
  .search-input {
    width: 100%;
    padding: 12px 16px;
    font-size: 16px;
    border: 1px solid #ddd;
    border-radius: 4px;
    outline: none;
    
    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  }
}

.search-results {
  padding: 0;
  overflow-y: auto;
  flex: 1;
  
  .search-loading, .search-no-results {
    padding: 20px;
    text-align: center;
    color: #999;
  }
  
  .search-results-list {
    .search-result-item {
      padding: 16px 20px;
      border-bottom: 1px solid #eee;
      cursor: pointer;
      transition: background-color 0.2s;
      
      &:hover {
        background-color: #f5f7fa;
      }
      
      :deep(.highlight) {
        background-color: #fffbe6;
        color: #d48806;
        padding: 1px 2px;
        border-radius: 2px;
        font-weight: bold;
      }
      
      .result-title {
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 4px;
        color: #1890ff;
      }
      
      .result-path {
        font-size: 12px;
        color: #999;
        margin-bottom: 8px;
      }
      
      .result-content {
        font-size: 14px;
        color: #666;
        line-height: 1.5;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .search-overlay {
    padding-top: 60px;
  }
  
  .search-modal {
    width: 90%;
    max-height: 70vh;
  }
}
</style> 